// src/components/AffiliateCompetitions.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Dialog,
    IconButton,
    Chip,
    Paper,
    useMediaQuery,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Edit, Delete, Visibility, CalendarToday, LocationOn, People, EmojiEvents } from '@mui/icons-material';
import { getAffiliateCompetitions, deleteCompetition } from '../api/competitionApi';
import CreateCompetitionDialog from './CreateCompetitionDialog';
import CompetitionDetailsView from './CompetitionDetailsView';

export default function AffiliateCompetitions({ affiliateId }) {
    const [competitions, setCompetitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [editingCompetition, setEditingCompetition] = useState(null);
    const [openDetailsView, setOpenDetailsView] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [competitionToDelete, setCompetitionToDelete] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        loadCompetitions();
    }, [affiliateId]);

    const loadCompetitions = async () => {
        try {
            setLoading(true);
            const data = await getAffiliateCompetitions(affiliateId);
            setCompetitions(data);
        } catch (error) {
            console.error('Error loading competitions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (competitionId) => {
        setCompetitionToDelete(competitionId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setCompetitionToDelete(null);
    };

    const handleDeleteCompetition = async () => {
        try {
            setErrorMessage('');
            await deleteCompetition(competitionToDelete);
            setDeleteConfirmOpen(false);
            setCompetitionToDelete(null);
            loadCompetitions();
        } catch (error) {
            console.error('Error deleting competition:', error);
            setErrorMessage('Failed to delete competition');
            setDeleteConfirmOpen(false);
        }
    };

    const handleViewCompetition = (competition) => {
        setSelectedCompetition(competition);
        setOpenDetailsView(true);
    };

    const handleEditCompetition = (competition, event) => {
        event.stopPropagation();
        setEditingCompetition(competition);
        setOpenCreateDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenCreateDialog(false);
        setEditingCompetition(null);
        loadCompetitions();
    };

    const formatDate = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        const startStr = start.toLocaleDateString('et-EE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });

        // Check if same day
        if (start.toDateString() === end.toDateString()) {
            return startStr;
        }

        const endStr = end.toLocaleDateString('et-EE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });

        return `${startStr} - ${endStr}`;
    };

    const isOngoing = (startDate, endDate) => {
        const now = new Date();
        return new Date(startDate) <= now && new Date(endDate) >= now;
    };

    const isUpcoming = (startDate) => {
        return new Date(startDate) > new Date();
    };

    const isPassed = (endDate) => {
        const now = new Date();
        const end = new Date(endDate);
        const daysDiff = Math.floor((now - end) / (1000 * 60 * 60 * 24));
        return daysDiff >= 7;
    };

    const CompetitionItem = ({ competition }) => {
        const ongoing = isOngoing(competition.startDate, competition.endDate);
        const upcoming = isUpcoming(competition.startDate);
        const passed = isPassed(competition.endDate);

        return (
            <Paper
                sx={{
                    p: { xs: 1.5, sm: 2 },
                    mb: 1.5,
                    cursor: 'pointer',
                    opacity: passed ? 0.7 : 1,
                    '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s'
                    }
                }}
                onClick={() => handleViewCompetition(competition)}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Header: Title and Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <EmojiEvents sx={{ color: 'primary.main', fontSize: 24, flexShrink: 0 }} />
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {competition.name}
                            </Typography>
                        </Box>

                        {/* Action Buttons - Always visible on right */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: { xs: 0.25, sm: 0.5 },
                                flexShrink: 0,
                                ml: 1
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => handleEditCompetition(competition, e)}
                                color="primary"
                                title="Edit"
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(competition.id);
                                }}
                                color="error"
                                title="Delete"
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Status Badges */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {ongoing && (
                            <Chip
                                label="ONGOING"
                                color="success"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                            />
                        )}
                        {upcoming && (
                            <Chip
                                label="UPCOMING"
                                color="info"
                                size="small"
                            />
                        )}
                        {passed && (
                            <Chip
                                label="PASSED"
                                size="small"
                                sx={{ bgcolor: 'grey.400', color: 'white' }}
                            />
                        )}
                        {competition.forMembersOnly && (
                            <Chip
                                label="MEMBERS ONLY"
                                size="small"
                                sx={{
                                    bgcolor: 'warning.main',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            />
                        )}
                    </Box>

                    {/* Date and Location */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.5, sm: 2 }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarToday sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {formatDate(competition.startDate, competition.endDate)}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {competition.location}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Registration Info - Stack on mobile */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.5, sm: 1 },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexWrap: 'wrap'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <People sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary">
                                {competition._count.registrations}/{competition.maxParticipants}
                            </Typography>
                        </Box>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: { xs: 'none', sm: 'block' } }}
                        >
                            •
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {competition.isTeamCompetition
                                ? `Team (${competition.teamSize})`
                                : 'Individual'}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: { xs: 'none', sm: 'block' } }}
                        >
                            •
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {competition._count.workouts} workouts
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: { xs: 'none', sm: 'block' } }}
                        >
                            •
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: { xs: 'normal', sm: 'nowrap' }
                            }}
                        >
                            {competition.organizer}
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        );
    };

    return (
        <Box>
            {errorMessage && (
                <Box sx={{ mb: 2 }}>
                    <Typography color="error" variant="body2">
                        {errorMessage}
                    </Typography>
                </Box>
            )}

            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1.5, sm: 0 },
                mb: 3
            }}>
                <Typography variant="h5" fontWeight="bold">
                    Competitions
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenCreateDialog(true)}
                    fullWidth={false}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Create Competition
                </Button>
            </Box>

            {loading ? (
                <Typography>Loading competitions...</Typography>
            ) : competitions.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                    <Typography align="center" color="textSecondary">
                        No competitions yet. Create your first competition!
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    {competitions.map((competition) => (
                        <CompetitionItem key={competition.id} competition={competition} />
                    ))}
                </Box>
            )}

            {/* Create/Edit Competition Dialog */}
            <CreateCompetitionDialog
                open={openCreateDialog}
                onClose={handleCloseDialog}
                affiliateId={affiliateId}
                competition={editingCompetition}
            />

            {/* Competition Details View Dialog */}
            <Dialog
                open={openDetailsView}
                onClose={() => setOpenDetailsView(false)}
                maxWidth="lg"
                fullWidth
                fullScreen={fullScreen}
            >
                {selectedCompetition && (
                    <CompetitionDetailsView
                        competitionId={selectedCompetition.id}
                        isAdminView={true}
                        onClose={() => setOpenDetailsView(false)}
                    />
                )}
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="delete-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="delete-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        Are you sure you want to delete this competition? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        color="primary"
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteCompetition}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}